import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { communityPosts as seedPosts } from "@/mocks/community";
import type { CommunityPost } from "@/types";
import Breadcrumb from "@/components/base/Breadcrumb";

const STORAGE_KEY = "loli-community-posts";

export default function Community() {
  useSeo({
    title: "社区交流 - 萝莉动漫主题站",
    description:
      "萝莉动漫主题站社区交流区，和同好们一起聊角色、追新番、分享本命故事。支持发帖与话题讨论，营造友善的二次元交流氛围。",
    keywords: "萝莉社区,二次元交流,动漫讨论,同好社区,发帖"
  });

  const [posts, setPosts] = useState<CommunityPost[]>(seedPosts);
  const [title, setTitle] = useState("");
  const [author, setAuthor] = useState("");
  const [content, setContent] = useState("");
  const [tip, setTip] = useState("");
  const [activePost, setActivePost] = useState<CommunityPost | null>(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const stored = JSON.parse(raw) as CommunityPost[];
        setPosts([...stored, ...seedPosts]);
      }
    } catch {
      /* ignore */
    }
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) {
      setTip("请填写帖子标题和内容");
      return;
    }
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, "0");
    const dateStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
    const post: CommunityPost = {
      id: `post-${Date.now()}`,
      title: title.trim(),
      author: author.trim() || "热心同好",
      avatar: "https://readdy.ai/api/search-image?query=cute%20pastel%20anime%20avatar%20icon%20of%20happy%20girl%2C%20soft%20pink%20background%2C%20kawaii%20anime%20style%2C%20circle%20portrait&width=200&height=200&seq=avatar-new&orientation=squarish",
      date: dateStr,
      content: content.trim(),
      replies: 0,
      likes: 0
    };
    const next = [post, ...posts];
    setPosts(next);
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "[]") as CommunityPost[];
      localStorage.setItem(STORAGE_KEY, JSON.stringify([post, ...stored]));
    } catch {
      /* ignore */
    }
    setTitle("");
    setAuthor("");
    setContent("");
    setTip("发布成功（演示环境，仅保存在本机）");
    setTimeout(() => setTip(""), 3000);
  };

  return (
    <div className="mx-auto max-w-[1280px] px-4 py-6 md:px-6 md:py-8">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "社区交流" }]} />

      <div className="mt-4 mb-6">
        <h1 className="text-2xl text-foreground-950 md:text-3xl">社区交流</h1>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
          这里是同好们的交流天地。聊角色、追新番、分享你的本命故事，友善发言，一起把热爱说出来。
        </p>
      </div>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_340px]">
        <div>
          <div className="mb-4 flex items-center justify-between">
            <h2 className="flex items-center gap-2 text-xl text-foreground-950">
              <i className="ri-chat-smile-2-line text-primary-500"></i>
              热门话题
              <span className="text-sm font-normal text-foreground-500">({posts.length})</span>
            </h2>
          </div>

          {posts.length > 0 ? (
            <div className="space-y-3">
              {posts.map((post) => (
                <button
                  key={post.id}
                  onClick={() => setActivePost(post)}
                  className="group flex w-full items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-4 text-left transition-all duration-200 hover:border-primary-200 hover:bg-background-100/60 cursor-pointer"
                >
                  <div className="h-11 w-11 shrink-0 overflow-hidden rounded-full bg-background-100">
                    <img src={post.avatar} alt={post.author} className="h-full w-full object-top" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-medium text-foreground-900 transition-colors group-hover:text-primary-600">
                      {post.title}
                    </h3>
                    <p className="mt-1 line-clamp-2 text-sm leading-relaxed text-foreground-600">
                      {post.content}
                    </p>
                    <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-foreground-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-user-line"></i>
                        {post.author}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line"></i>
                        {post.date}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-chat-1-line"></i>
                        {post.replies} 回复
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-heart-3-line"></i>
                        {post.likes}
                      </span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-background-300 bg-background-100/60 px-6 py-16 text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-primary-500">
                <i className="ri-chat-new-line text-3xl"></i>
              </div>
              <h3 className="font-heading text-lg text-foreground-900">暂无交流内容，快来发帖吧</h3>
              <p className="mt-2 text-sm text-foreground-600">成为第一个发起话题的人</p>
            </div>
          )}
        </div>

        <aside>
          <div className="sticky top-20 rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="mb-4 flex items-center gap-2 font-heading text-base text-foreground-950">
              <i className="ri-edit-2-line text-accent-500"></i>
              发布新帖
            </h2>
            <form onSubmit={handleSubmit} className="space-y-3">
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={50}
                placeholder="帖子标题"
                className="w-full rounded-md border border-background-300 bg-background-100 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-500 focus:border-primary-300 focus:outline-none"
              />
              <input
                value={author}
                onChange={(e) => setAuthor(e.target.value)}
                maxLength={20}
                placeholder="你的昵称（可选）"
                className="w-full rounded-md border border-background-300 bg-background-100 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-500 focus:border-primary-300 focus:outline-none"
              />
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value.slice(0, 500))}
                rows={4}
                placeholder="说点什么吧～"
                className="w-full resize-none rounded-md border border-background-300 bg-background-100 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-500 focus:border-primary-300 focus:outline-none"
              />
              <div className="flex items-center justify-between text-xs text-foreground-500">
                <span>{content.length} / 500</span>
                <span>纯前端演示</span>
              </div>
              <button
                type="submit"
                className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer dark:text-foreground-950"
              >
                <i className="ri-send-plane-line"></i>
                发布帖子
              </button>
              {tip && <p className="text-xs text-primary-600">{tip}</p>}
            </form>
            <p className="mt-4 border-t border-background-200 pt-4 text-xs leading-relaxed text-foreground-500">
              温馨提示：本站为演示项目，社区发帖与评论均为纯前端模拟，仅保存在你的本机浏览器中，不提供后端服务。
            </p>
          </div>
        </aside>
      </div>

      {activePost && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-foreground-950/40 px-4 py-8"
          onClick={() => setActivePost(null)}
        >
          <div
            className="max-h-[80vh] w-full max-w-2xl overflow-y-auto rounded-lg border border-background-200 bg-background-50 p-6 animate-fade-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-start justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 overflow-hidden rounded-full bg-background-100">
                  <img src={activePost.avatar} alt={activePost.author} className="h-full w-full object-top" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-900">{activePost.author}</p>
                  <p className="text-xs text-foreground-500">{activePost.date}</p>
                </div>
              </div>
              <button
                onClick={() => setActivePost(null)}
                className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-100 cursor-pointer"
                aria-label="关闭"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <h2 className="text-xl leading-snug text-foreground-950">{activePost.title}</h2>
            <p className="mt-4 whitespace-pre-wrap text-sm leading-loose text-foreground-700">
              {activePost.content}
            </p>
            <div className="mt-6 flex items-center gap-4 border-t border-background-200 pt-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-chat-1-line"></i>
                {activePost.replies} 回复
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-heart-3-line"></i>
                {activePost.likes}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}